import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { cn } from "@/app/_utils/cn";
import { Search, ChevronDown, List, Calendar } from "lucide-react";
import { SHOW_STATUS_LABEL, SHOW_STATUS_DOT_CLASSES, ShowStatus } from "./showStatus";
import type { ViewMode } from "../showsPageModel";

const STATUS_OPTIONS: (ShowStatus | "all")[] = [
  "all",
  "on-sale",
  "almost-sold-out",
  "sold-out",
  "draft",
  "past",
];

export default function ShowsToolbar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  viewMode,
  onViewModeChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: ShowStatus | "all";
  onStatusFilterChange: (value: ShowStatus | "all") => void;
  viewMode: ViewMode;
  onViewModeChange: (value: ViewMode) => void;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative max-w-[420px] flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <Search className="size-3.5 text-muted-foreground" />
        </div>
        <Input
          type="search"
          placeholder="Search shows…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-[30px] pl-9 text-[12.5px]"
          aria-label="Search shows"
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-3 text-[12.5px] text-foreground hover:bg-muted"
          >
            <span className="text-muted-foreground">Status:</span>
            {statusFilter !== "all" && (
              <span className={`size-2 flex-none rounded-sm ${SHOW_STATUS_DOT_CLASSES[statusFilter]}`} />
            )}
            {statusFilter === "all" ? "All statuses" : SHOW_STATUS_LABEL[statusFilter]}
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {STATUS_OPTIONS.map((option) => (
            <DropdownMenuItem key={option} onClick={() => onStatusFilterChange(option)}>
              {option !== "all" && (
                <span className={`size-2 flex-none rounded-sm ${SHOW_STATUS_DOT_CLASSES[option]}`} />
              )}
              {option === "all" ? "All statuses" : SHOW_STATUS_LABEL[option]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="ml-auto hidden gap-0.5 rounded-md border border-border bg-muted p-0.5 sm:flex">
        <button
          type="button"
          onClick={() => onViewModeChange("calendar")}
          className={cn(
            "flex h-[26px] items-center gap-1.5 rounded px-2.5 text-xs font-medium",
            viewMode === "calendar"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground"
          )}
        >
          <Calendar className="size-3.5" />
          Calendar
        </button>
        <button
          type="button"
          onClick={() => onViewModeChange("list")}
          className={cn(
            "flex h-[26px] items-center gap-1.5 rounded px-2.5 text-xs font-medium",
            viewMode === "list"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground"
          )}
        >
          <List className="size-3.5" />
          List
        </button>
      </div>
    </div>
  );
}
